import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SectionHeader, StatusBadge, Field, inputCls } from "@/components/Shared";
import { fmtDate } from "@/lib/format";
import DataTable from "@/components/DataTable";
import { InlineEditSelect } from "@/components/InlineEdit";
import HealthBadge from "@/components/HealthBadge";
import HelpHint from "@/components/HelpHint";
import BulkImportButton from "@/components/BulkImportButton";
import { usePermissions } from "@/contexts/PermissionsContext";
import DepartmentPicker, { DepartmentBadge } from "@/components/DepartmentPicker";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import FirmBadge from "@/components/FirmBadge";
import { useFirms } from "@/contexts/FirmContext";
import VehicleFormWizard, { VEHICLE_EMPTY } from "@/components/VehicleFormWizard";
import { Plus, Pencil, Trash2, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const EMPTY = VEHICLE_EMPTY;

export default function Fuhrpark() {
  const { can } = usePermissions();
  const canCreate = can("fuhrpark", "create");
  const canEdit = can("fuhrpark", "edit");
  const canDelete = can("fuhrpark", "delete");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [deptFilter, setDeptFilter] = useDepartmentFilter("fuhrpark");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulk, setBulk] = useState({ status: "", workplace: "", department_id: "", firm_id: "" });
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  async function load() {
    setLoading(true);
    try {
      const params = { q, status };
      if (deptFilter) params.department_id = deptFilter;
      const { data } = await api.get("/vehicles", { params });
      setItems(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [q, status, deptFilter]);

  useEffect(() => {
    if (sp.get("new") === "1") {
      setEditing(null); setForm(EMPTY); setShowForm(true);
      sp.delete("new"); setSp(sp, { replace: true });
    }
    // Phase 32b — Drill-down from Dashboard
    const urlStatus = sp.get("status");
    if (urlStatus !== null && urlStatus !== status) setStatus(urlStatus);
    const urlDept = sp.get("department_id");
    if (urlDept !== null && urlDept !== deptFilter) setDeptFilter(urlDept);
  }, [sp]);

  function toggle(vid) { setSelected((s) => s.includes(vid) ? s.filter((x) => x !== vid) : [...s, vid]); }
  function toggleAll() { setSelected(selected.length === items.length ? [] : items.map((v) => v.id)); }

  async function applyBulk() {
    const payload = { ids: selected };
    if (bulk.status) payload.status = bulk.status;
    if (bulk.workplace) payload.workplace = bulk.workplace;
    if (bulk.department_id) payload.department_id = bulk.department_id;
    // "__pool__" magic-value demotes to the cross-firm Pool (firm_id="")
    if (bulk.firm_id) payload.firm_id = bulk.firm_id === "__pool__" ? "" : bulk.firm_id;
    if (!bulk.status && !bulk.workplace && !bulk.department_id && !bulk.firm_id) { toast.error("Keine Änderungen"); return; }
    try {
      const { data } = await api.patch("/vehicles/bulk", payload);
      toast.success(`${data.modified} Fahrzeuge aktualisiert`);
      setSelected([]); setShowBulk(false); setBulk({ status: "", workplace: "", department_id: "", firm_id: "" }); load();
    } catch { toast.error("Fehler"); }
  }

  async function save() {
    try {
      const payload = { ...form, current_km: +form.current_km || 0, adblue_percent: +form.adblue_percent || 0 };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload); else await api.post("/vehicles", payload);
      toast.success(editing ? "Fahrzeug aktualisiert" : "Fahrzeug angelegt");
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch { toast.error("Fehler beim Speichern"); }
  }
  async function remove(id) {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    try { await api.delete(`/vehicles/${id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  }
  async function inlineStatus(id, newStatus) {
    await api.put(`/vehicles/${id}`, { status: newStatus });
    setItems((cur) => cur.map((v) => v.id === id ? { ...v, status: newStatus } : v));
    toast.success(`Status: ${newStatus}`);
  }

  return (
    <div className="p-6 md:p-8" data-testid="fuhrpark-page">
      <SectionHeader title="Fuhrpark" subtitle="Fahrzeuge, §57a, Versicherung, Service"
        actions={
          <div className="flex gap-2">
            {selected.length > 0 && (
              <button data-testid="bulk-veh-btn" onClick={() => setShowBulk(true)}
                className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm font-medium px-4 py-2 rounded-md hover:bg-amber-500/20">
                Massenaktion ({selected.length})
              </button>
            )}
            {canCreate && (
              <BulkImportButton
                entity="vehicles"
                templateUrl="/imports/vehicles/template"
                uploadUrl="/imports/vehicles"
                templateFileName="Fahrzeuge_Vorlage.xlsx"
                label="Bulk-Import"
                testid="bulk-import-vehicles"
                onDone={load}
              />
            )}
            {canCreate && (
              <button data-testid="add-vehicle-btn" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
                <Plus className="w-4 h-4" /> Fahrzeug hinzufügen
              </button>
            )}
            <button
              data-testid="export-fahrzeugliste-btn"
              onClick={async () => {
                try {
                  const res = await api.get("/exports/fahrzeugliste-kodlogy", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Fahrzeugliste_${new Date().toISOString().slice(0,10)}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Fahrzeugliste heruntergeladen");
                } catch (e) {
                  toast.error("Export fehlgeschlagen");
                }
              }}
              className="inline-flex items-center gap-2 bg-emerald-600/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/25 text-sm font-medium px-4 py-2 rounded-md transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" /> Excel-Export
            </button>
          </div>
        } />

      <HelpHint title="Wie funktioniert der Fuhrpark?" className="mb-4" testid="help-fuhrpark">
        <p>Hier verwaltest du alle Fahrzeuge mit <b>Kennzeichen, VIN, §57a-Pickerl, Vignette, Versicherung, Service-Daten</b> und Dokumenten. Klicke auf eine Zeile für das Detail-Profil mit Compliance-Anzeige.</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><b>Status</b>: <i>aktiv / parkend / werkstatt / inaktiv</i> — beeinflusst Verfügbarkeit für Fahrer-Übergaben.</li>
          <li><b>Pickerl §57a</b> & <b>Vignette</b>: Ablauf-Daten lösen automatische Warnungen im Dashboard aus (rot = abgelaufen, gelb = ≤30d).</li>
          <li><b>Excel-Export</b> oben rechts: Kodlogy-konforme Fahrzeugliste — direkt importierbar in dein altes System.</li>
          <li><b>Abteilung</b> steuert die Sichtbarkeit für Disponenten — UPS-Disponent sieht nur UPS-Fahrzeuge.</li>
          <li><b>Massenaktion</b> erscheint bei Markierung — Status, Abteilung oder Service-Daten in einem Schritt ändern.</li>
        </ul>
      </HelpHint>

      <DataTable
        rows={items}
        loading={loading}
        searchTerm={q}
        onSearchChange={setQ}
        searchPlaceholder="Kennzeichen, Marke, VIN..."
        testid="fuhrpark-table"
        emptyTitle="Keine Fahrzeuge gefunden"
        emptyHint={q || status ? "Versuchen Sie andere Filter." : "Fügen Sie das erste Fahrzeug hinzu."}
        filters={
          <div className="flex items-center gap-2 flex-wrap">
            <FirmFilter compact />
            <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="fuhrpark" testid="fuhrpark-dept-filter"/>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="fuhrpark-status-filter"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
              <option value="">Alle Status</option>
              <option value="aktiv">Aktiv</option>
              <option value="werkstatt">Werkstatt</option>
              <option value="inaktiv">Inaktiv</option>
            </select>
          </div>
        }
        columns={[
          {
            key: "_select",
            label: (
              <input
                type="checkbox"
                checked={items.length > 0 && selected.length === items.length}
                onChange={toggleAll}
                onClick={(e) => e.stopPropagation()}
                data-testid="fuhrpark-select-all"
                className="rounded cursor-pointer"
              />
            ),
            headerClassName: "w-8",
            render: (v) => (<input type="checkbox" checked={selected.includes(v.id)} onChange={() => toggle(v.id)} onClick={(e) => e.stopPropagation()} data-testid={`veh-select-${v.id}`} className="rounded" />),
          },
          { key: "license_plate", label: "Kennzeichen", sortable: true,
            render: (v) => <span className="font-mono text-zinc-100 font-medium cursor-pointer" onClick={() => navigate(`/fuhrpark/${v.id}`)}>{v.license_plate}</span> },
          { key: "brand", label: "Fahrzeug", sortable: true,
            render: (v) => <span className="text-zinc-300 cursor-pointer" onClick={() => navigate(`/fuhrpark/${v.id}`)}>{v.brand} {v.model} <span className="text-zinc-500">· {v.vehicle_type}</span></span> },
          { key: "department", label: "Abteilung", sortable: true, render: (v) => (
            v.department_id
              ? <DepartmentBadge id={v.department_id}/>
              : <span className="text-zinc-400">{v.department || "—"}</span>
          ) },
          { key: "firm_id", label: "Firma", sortable: true, render: (v) => <FirmBadge firmId={v.firm_id} testid={`veh-firm-${v.id}`}/> },
          { key: "current_km", label: "KM", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-300",
            render: (v) => (v.current_km || 0).toLocaleString("de-AT") },
          { key: "adblue_percent", label: "AdBlue", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums",
            render: (v) => <span className={v.adblue_percent < 20 ? "text-red-400" : v.adblue_percent < 40 ? "text-amber-400" : "text-emerald-400"}>{v.adblue_percent}%</span> },
          { key: "pickerl_date", label: "Pickerl", sortable: true, render: (v) => <span className="text-zinc-400 font-mono text-xs">{fmtDate(v.pickerl_date)}</span> },
          {
            key: "_health", label: "Health",
            render: (v) => (
              <HealthBadge
                level={v.health_level || "ok"}
                reasons={v.health_reasons || []}
                size="xs"
                variant="badge"
                testid={`veh-health-${v.id}`}
              />
            ),
          },
          { key: "status", label: "Status", sortable: true, render: (v) => (
            canEdit ? (
              <InlineEditSelect
                value={v.status}
                options={[["aktiv","Aktiv"],["werkstatt","Werkstatt"],["inaktiv","Inaktiv"]]}
                onSave={(newV) => inlineStatus(v.id, newV)}
                render={(s) => <StatusBadge status={s}/>}
                testid={`veh-status-${v.id}`}
              />
            ) : (
              <StatusBadge status={v.status}/>
            )
          ) },
          {
            key: "_actions", label: "",
            headerClassName: "text-right", className: "text-right",
            render: (v) => (
              <>
                {canEdit && <button onClick={(e) => { e.stopPropagation(); setEditing(v); setForm({ ...EMPTY, ...v }); setShowForm(true); }} data-testid={`edit-veh-${v.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5" /></button>}
                {canDelete && <button onClick={(e) => { e.stopPropagation(); remove(v.id); }} data-testid={`delete-veh-${v.id}`} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>}
                {!canEdit && !canDelete && <span className="text-zinc-600 text-xs">—</span>}
              </>
            ),
          },
        ]}
      />

      {showForm && <VehicleFormWizard form={form} setForm={setForm} onClose={() => setShowForm(false)} onSave={save} editing={!!editing} />}

      {showBulk && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Massenaktion</div>
                <h3 className="font-display font-semibold text-zinc-50 mt-1">{selected.length} Fahrzeuge aktualisieren</h3>
              </div>
              <button onClick={() => setShowBulk(false)} className="text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Status setzen auf">
                <select value={bulk.status} onChange={(e) => setBulk({ ...bulk, status: e.target.value })} className={inputCls}>
                  <option value="">— unverändert —</option>
                  {["aktiv", "werkstatt", "inaktiv"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Einsatzort setzen auf">
                <input value={bulk.workplace} onChange={(e) => setBulk({ ...bulk, workplace: e.target.value })} placeholder="— unverändert —" className={inputCls} />
              </Field>
              <Field label="Abteilung setzen auf">
                <DepartmentPicker value={bulk.department_id} onChange={(id) => setBulk({ ...bulk, department_id: id })} testid="bulk-veh-dept-picker"/>
              </Field>
              <Field label="Firma setzen auf">
                <FirmBulkSelect value={bulk.firm_id} onChange={(v) => setBulk({ ...bulk, firm_id: v })} />
              </Field>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={applyBulk} data-testid="bulk-veh-apply" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Anwenden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Phase F — Tiny firm-picker used in the bulk-update modal. "" = no change,
// "__pool__" = demote to cross-firm Pool, otherwise a real firm id.
function FirmBulkSelect({ value, onChange }) {
  const { firms } = useFirms();
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid="bulk-veh-firm-picker"
      className={inputCls}
    >
      <option value="">— unverändert —</option>
      <option value="__pool__">Pool (firmen­übergreifend)</option>
      {(firms || []).map((f) => (
        <option key={f.id} value={f.id}>{f.name}</option>
      ))}
    </select>
  );
}

